<script setup>
import EmailingDetailedStatisticsTable from '@frontJs/components/tables/projectManagement/modules/EmailingDetailedStatisticsTable.vue';
import BasketIcon from '@iconify-vue/ph/basket';
import CoinsIcon from '@iconify-vue/ph/coins';
import EnvelopeOpenIcon from '@iconify-vue/ph/envelope-open';
import EyeIcon from '@iconify-vue/ph/eye';
import MegaphoneIcon from '@iconify-vue/ph/megaphone';
import MouseLeftClickIcon from '@iconify-vue/ph/mouse-left-click';
import ProhibitIcon from '@iconify-vue/ph/prohibit';
import ShieldWarningIcon from '@iconify-vue/ph/shield-warning';
import ShoppingCartSimpleIcon from '@iconify-vue/ph/shopping-cart-simple';
import WarningIcon from '@iconify-vue/ph/warning';
import CardColumn from '@js/components/cards/CardColumn.vue';
import CardGrid from '@js/components/cards/CardGrid.vue';
import MetricCard from '@js/components/cards/MetricCard.vue';
import LineChart from '@js/components/charts/LineChart.vue';
import DialogModal from '@js/components/dialogs/DialogModal.vue';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import { useRoute } from '@js/utils/route';
import { toastError } from '@js/utils/toast';
import { h, ref, watch } from 'vue';

const { tenant, emailing } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    emailing: {
        type: Object,
        required: true,
    },
});

const { route } = useRoute();

const isOpen = ref(false);

const loading = ref(true);

const data = ref({});

function updateData() {
    loading.value = true;

    axios
        .post(
            route('front.projectManagement.modules.postEmailingStatistics', {
                tenant: tenant.id,
                emailing: emailing.id,
            }),
        )
        .then((response) => {
            data.value = response.data;
        })
        .finally(() => {
            loading.value = false;
        })
        .catch((error) => {
            toastError(error);
        });
}

watch(
    () => isOpen.value,
    (newValue) => {
        if (newValue) {
            loading.value = true;
            updateData();
        }
    },
);
</script>

<template>
    <DialogModal v-model="isOpen" size="extra-large" :loading="loading">
        <template #trigger>
            <slot name="trigger" />
        </template>

        <template #title>
            Statistiques de l'emailing "{{ emailing.name }}"
        </template>

        <template #full-content>
            <TabLayout
                :tabs="{
                    general: {
                        title: 'Général',
                        contentSlotName: 'general',
                    },
                    detailed: {
                        title: 'Détaillé',
                        contentSlotName: 'detailed',
                    },
                }"
                :sync-url="false"
            >
                <template #general>
                    <div class="p-4">
                        <CardColumn>
                            <CardGrid>
                                <MetricCard
                                    title="Emails envoyés"
                                    subtitle="Nombre d'emails envoyés"
                                    :value="data.sent"
                                    :with-decimals="false"
                                    :icon="EnvelopeOpenIcon"
                                />

                                <MetricCard
                                    title="Taux d'ouverture"
                                    subtitle="Ratio ouvertures sur emails envoyés"
                                    :value="(data.opened / data.sent) * 100"
                                    type="percentage"
                                    :with-decimals="true"
                                    :icon="EyeIcon"
                                />

                                <MetricCard
                                    title="Taux de clics"
                                    subtitle="Ratio clics sur emails envoyés"
                                    :value="(data.clicked / data.sent) * 100"
                                    type="percentage"
                                    :with-decimals="true"
                                    :icon="MouseLeftClickIcon"
                                />
                            </CardGrid>

                            <CardGrid>
                                <MetricCard
                                    title="Taux de désabonnement"
                                    subtitle="Ratio désinscriptions sur emails envoyés"
                                    :value="
                                        (data.unsubscribed / data.sent) * 100
                                    "
                                    type="percentage"
                                    :with-decimals="true"
                                    :icon="ProhibitIcon"
                                />

                                <MetricCard
                                    title="Taux de blocage"
                                    subtitle="Ratio emails bloqués sur emails envoyés"
                                    :value="(data.blocked / data.sent) * 100"
                                    type="percentage"
                                    :with-decimals="true"
                                    :icon="ShieldWarningIcon"
                                />

                                <MetricCard
                                    title="Taux de rebond"
                                    subtitle="Ratio emails en rebond sur emails envoyés"
                                    :value="(data.bounced / data.sent) * 100"
                                    type="percentage"
                                    :with-decimals="true"
                                    :icon="WarningIcon"
                                />

                                <MetricCard
                                    title="Taux de spam"
                                    subtitle="Ratio signalements spam sur emails envoyés"
                                    :value="(data.spam / data.sent) * 100"
                                    type="percentage"
                                    :with-decimals="true"
                                    :icon="MegaphoneIcon"
                                />
                            </CardGrid>

                            <CardGrid>
                                <MetricCard
                                    title="Commandes"
                                    subtitle="Commandes liées aux emailings"
                                    :value="data.orders"
                                    suffix-value="(total)"
                                    :compare-data="[
                                        {
                                            value: data.ordersAfterOpening,
                                            suffix: '(après ouverture)',
                                        },
                                        {
                                            value: data.ordersAfterClick,
                                            suffix: '(après clic)',
                                        },
                                    ]"
                                    :with-decimals="false"
                                    :icon="ShoppingCartSimpleIcon"
                                    :compare="false"
                                />

                                <MetricCard
                                    title="CA TTC"
                                    subtitle="CA TTC lié aux emailings"
                                    :value="data.revenue"
                                    suffix-value="(total)"
                                    :compare-data="[
                                        {
                                            value: data.revenueAfterOpening,
                                            suffix: '(après ouverture)',
                                        },
                                        {
                                            value: data.revenueAfterClick,
                                            suffix: '(après clic)',
                                        },
                                    ]"
                                    type="price"
                                    :icon="CoinsIcon"
                                    :compare="false"
                                    :with-decimals="false"
                                />

                                <MetricCard
                                    title="Panier moyen"
                                    subtitle="Panier moyen lié aux emailings"
                                    :value="data.averageBasket"
                                    suffix-value="(total)"
                                    :compare-data="[
                                        {
                                            value: data.averageBasketAfterOpening,
                                            suffix: '(après ouverture)',
                                        },
                                        {
                                            value: data.averageBasketAfterClick,
                                            suffix: '(après clic)',
                                        },
                                    ]"
                                    type="price"
                                    :icon="BasketIcon"
                                    :compare="false"
                                    :with-decimals="false"
                                />
                            </CardGrid>

                            <LineChart
                                :datasets="[
                                    {
                                        group: 'CA TTC',
                                        label: 'CA TTC',
                                        backgroundColor: '#4d65b9',
                                        borderColor: '#4d65b9',
                                        pointRadius: 4,
                                        pointHoverRadius: 5,
                                        fill: false,
                                        data: data.turnoverChartData.baseData,
                                        labels: data.turnoverChartData
                                            .baseLabels,
                                        yAxisID: 'y',
                                    },
                                ]"
                                title="CA TTC lié aux emailings (après ouverture)"
                                type-axis1="price"
                                :with-decimals-axis1="false"
                            />
                        </CardColumn>
                    </div>
                </template>

                <template #detailed>
                    <div class="p-4">
                        <EmailingDetailedStatisticsTable
                            :tenant="tenant"
                            :emailing="emailing"
                        />
                    </div>
                </template>
            </TabLayout>
        </template>
    </DialogModal>
</template>
